import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { IUser, IEventSummary, ICourseSummary, IClub, IUserWaitlistSummary } from "@eagerdog/interfaces";
import { Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { Helmet } from "react-helmet";
import moment from "moment";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";

import SubSearchInput from "./SubSearchInput/SubSearchInput";
import DiscoverCarousel from "./DiscoverCarousel/DiscoverCarousel";
import UserCard from "./UserCard/UserCard";
import ProfileSummaryCard from "./ProfileSummaryCard/ProfileSummaryCard";
import GetStartedCard from "./GetStartedCard/GetStartedCard";
import QuickActions from "./QuickActions/QuickActions";
import Feed from "./Feed/Feed";
import MyDogs from "./MyDogs/MyDogs";
import RecentActivity from "./RecentActivity/RecentActivity";
import MyClubs from "./MyClubs/MyClubs";
import MyResults from "./MyResults/MyResults";
import RecentEntries from "./RecentEntries/RecentEntries";
import DiscoverFooter from "./DiscoverFooter/DiscoverFooter";

import Icon from "src/components/Icon/Icon";

import styles from "./Discover.module.scss";

const BATCH_SIZE = 6;

const Discover: React.FC = () => {
    const navigate = useNavigate();
    const user: IUser = helperService.getUser();
    const isLoggedIn = !!user._id;
    // Dog Owner Premium is retired: no upsell and no Premium/Basic badge for owners.
    const isPremiumMember = !DOG_OWNER_PREMIUM_ENABLED || !!user.tariff_plan_subscription_id;
    const club = helperService.getClub();
    const isClubManager = user.user_type === Constants.user_type.club_manager;

    const [searchDate, setSearchDate] = useState<Date>(new Date());
    const [category, setCategory] = useState<string>("Events");
    const [clubName, setClubName] = useState<string>("");
    const clubNameRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [feedRefreshKey, setFeedRefreshKey] = useState<number>(0);

    useEffect(() => {
        if (category === "Clubs") {
            setTimeout(() => clubNameRef.current?.focus(), 0);
        } else {
            setClubName("");
        }
    }, [category]);

    const [events, setEvents] = useState<IEventSummary[]>([]);
    const [eventsSkip, setEventsSkip] = useState<number>(0);
    const [eventsHasMore, setEventsHasMore] = useState<boolean>(true);

    const [courses, setCourses] = useState<ICourseSummary[]>([]);
    const [coursesSkip, setCoursesSkip] = useState<number>(0);
    const [coursesHasMore, setCoursesHasMore] = useState<boolean>(true);

    const [clubs, setClubs] = useState<IClub[]>([]);

    // Recent Entries sidebar. The same response carries `next_up` for a
    // future announcements/notifications banner.
    const [recentEntries, setRecentEntries] = useState<IUserWaitlistSummary[]>([]);

    const loadEntries = useCallback(() => {
        if (!user._id) return;
        apiService.searchUserEntries(user._id, {
            period: "recent",
            $skip: 0,
            $limit: 3,
            today: moment().format("YYYY-MM-DD"),
        }).then((page) => {
            setRecentEntries(page?.items || []);
        }).catch(() => {
            // sidebar widgets are non-critical
        });
    }, [user._id]);

    useEffect(() => {
        if (isLoggedIn && !isClubManager) loadEntries();
    }, [isLoggedIn, isClubManager, loadEntries]);

    const buildQuery = (skip: number): any => ({
        sort: [{ attribute_name: "start_date", sort: "asc" }],
        $limit: BATCH_SIZE,
        $skip: skip,
        query: {
            $and: [{
                attribute_name: "end_date",
                attribute_value: { operator: "$gte", value: new Date() }
            }],
            $or: []
        }
    });

    useEffect(() => {
        if (isClubManager) {
            const savedClub = helperService.getClub();
            if (savedClub?._id) {
                navigate(`/k/${savedClub._id}`);
            } else {
                apiService.getCurrentClub().then((club) => {
                    navigate(`/k/${club._id}`);
                });
            }
            return;
        }

        if (isLoggedIn) return;

        const initialLoad = async () => {
            setLoading(true);
            try {
                const [eventsData, coursesData, clubsResult] = await Promise.all([
                    apiService.getPublicEvents(buildQuery(0)),
                    apiService.getCourses(buildQuery(0)),
                    apiService.getClubs({ $limit: 100 })
                ]);

                setEvents(eventsData || []);
                setEventsHasMore((eventsData || []).length === BATCH_SIZE);
                setEventsSkip(BATCH_SIZE);

                setCourses(coursesData || []);
                setCoursesHasMore((coursesData || []).length === BATCH_SIZE);
                setCoursesSkip(BATCH_SIZE);

                const allClubs: IClub[] = (clubsResult?.clubs || []);
                setClubs(allClubs.filter((c: IClub) => c.cover_image_url && c.logo_url && c.description));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const loadMoreEvents = async () => {
        if (!eventsHasMore) return;
        try {
            const data = await apiService.getPublicEvents(buildQuery(eventsSkip));
            const items: IEventSummary[] = data || [];
            setEvents(prev => [...prev, ...items]);
            setEventsHasMore(items.length === BATCH_SIZE);
            setEventsSkip(prev => prev + BATCH_SIZE);
        } catch (err) {
            console.error(err);
        }
    };

    const loadMoreCourses = async () => {
        if (!coursesHasMore) return;
        try {
            const data = await apiService.getCourses(buildQuery(coursesSkip));
            const items: ICourseSummary[] = data || [];
            setCourses(prev => [...prev, ...items]);
            setCoursesHasMore(items.length === BATCH_SIZE);
            setCoursesSkip(prev => prev + BATCH_SIZE);
        } catch (err) {
            console.error(err);
        }
    };

    if (isClubManager && club._id) {
        return <Navigate to={`/k/${club._id}`} replace />;
    }

    const handleSearch = () => {
        const dateParam = searchDate.toISOString().split("T")[0];
        if (category === "Courses") {
            navigate(`/courses?date=${dateParam}`);
        } else if (category === "Clubs") {
            const trimmed = clubName.trim();
            navigate(trimmed ? `/kennel-clubs?name=${encodeURIComponent(trimmed)}` : "/kennel-clubs");
        } else {
            navigate(`/events?date=${dateParam}`);
        }
    };

    if (isLoggedIn) {
        return (
            <>
            <Helmet>
                <title>Eager Dog &middot; Home</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <div className={styles.HomeShell}>
                <div className={styles.homeGrid}>
                    <div className={styles.homeLeft}>
                        {/* InvitesCard returns once invites exist; keep the component for then. */}
                        <ProfileSummaryCard />
                        <GetStartedCard onPosted={() => setFeedRefreshKey((k) => k + 1)} />
                        {!isPremiumMember && (
                            <div className={styles.premiumCta}>
                                <div className={styles.inner}>
                                    <div className={styles.icon}>
                                        <Icon className={styles.star} type="star" />
                                    </div>
                                    <div className={styles.text}>
                                        <span className={styles.small}>Become a</span>
                                        <span className={styles.large}>Premium Member</span>
                                    </div>
                                </div>
                                <div className={styles.action}>
                                    <Link to="/premium" className={styles.white}>Learn More</Link>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.homeMain}>
                        <MyDogs />
                        <QuickActions onPosted={() => setFeedRefreshKey((k) => k + 1)} />
                        <Feed key={feedRefreshKey} />
                    </div>
                    <div className={styles.homeRight}>
                        <div className={`${styles.rightCard} ${styles.desktopOnly}`}>
                            <RecentEntries entries={recentEntries} onChanged={loadEntries} />
                        </div>
                        <div className={`${styles.rightCard} ${styles.desktopOnly}`}><MyResults /></div>
                        <div className={`${styles.rightCard} ${styles.desktopOnly}`}><MyClubs /></div>
                    </div>
                </div>
            </div>
            <DiscoverFooter />
            </>
        );
    }

    return (
        <>
        <div className={styles.Discover}>
            <Helmet>
                <title>Eager Dog &middot; Dog Sport Events & Courses</title>
                <meta name="description" content="Discover kennel club events & courses on Eager Dog, the all-in-one platform for dog sport clubs and owners." />
                <meta name="robots" content="index, follow" />
            </Helmet>
            <div className={styles.searchWrap}>
                <div className={styles.inner}>
                    <div className={styles.search}>
                        <div className={styles.searchInner}>
                            <SubSearchInput
                                label="Category"
                                placeholder="Choose a Category"
                                value={category}
                                isDate={false}
                                options={["Events", "Courses", "Clubs"]}
                                onChange={(v: string) => setCategory(v)}
                            />
                            {category === "Clubs" ? (
                                <div className={styles.clubInput}>
                                    <span className={styles.clubInputLabel}>Club Name</span>
                                    <input
                                        ref={clubNameRef}
                                        className={styles.clubInputField}
                                        type="text"
                                        placeholder="Search by club name"
                                        value={clubName}
                                        onChange={(e) => setClubName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                                    />
                                </div>
                            ) : (
                                <SubSearchInput
                                    className={styles.dateSelect}
                                    label="Date"
                                    placeholder="Choose a Start Date"
                                    value={searchDate}
                                    isLast={true}
                                    isDate={true}
                                    onChange={(d: Date) => setSearchDate(d)}
                                />
                            )}
                            <div className={styles.searchButton} onClick={handleSearch}>
                                <Icon type="search" className={styles.searchIcon} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.main}>
                <div className={styles.listings}>
                    <DiscoverCarousel
                        title="Upcoming Events"
                        items={events}
                        type="event"
                        linkName="View All Events"
                        link="/events"
                        loading={loading}
                        hasMore={eventsHasMore}
                        onLoadMore={loadMoreEvents}
                    />
                    <DiscoverCarousel
                        title="Upcoming Courses"
                        items={courses}
                        type="course"
                        linkName="View All Courses"
                        link="/courses"
                        loading={loading}
                        hasMore={coursesHasMore}
                        onLoadMore={loadMoreCourses}
                    />
                    <DiscoverCarousel
                        title="Popular Clubs"
                        items={clubs}
                        type="club"
                        linkName="View All Clubs"
                        link="/kennel-clubs"
                        loading={loading}
                    />
                </div>
                <div className={styles.sidebar}>
                    <div className={styles.sidebarInner}>
                        {/* Guest layout only: signed-in users returned above with the home grid. */}
                        <UserCard user={user} isPremium={isPremiumMember} showPremiumBadge={DOG_OWNER_PREMIUM_ENABLED} isGuest={true} />
                        <RecentActivity />
                    </div>
                </div>
            </div>
        </div>
        <DiscoverFooter />
        </>
    );
};

export default Discover;
